import { useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { useUIStore } from '@/stores/useUIStore';
import { createAppTheme } from './theme';
import type { ColorMode } from './theme';

/** Tracks the OS colour scheme so the `system` preference stays live. */
function useSystemColorMode(): ColorMode {
  const [mode, setMode] = useState<ColorMode>(() =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (event: MediaQueryListEvent): void => setMode(event.matches ? 'dark' : 'light');
    query.addEventListener('change', handler);
    return () => query.removeEventListener('change', handler);
  }, []);

  return mode;
}

/**
 * Resolves the theme preference into a concrete MUI theme.
 *
 * The theme object is memoised on the mode alone: `createAppTheme` walks every
 * component override, so rebuilding it on unrelated renders would be wasteful
 * and would break Emotion's cache reuse.
 */
export function AppThemeProvider({ children }: PropsWithChildren): JSX.Element {
  const preference = useUIStore((state) => state.themePreference);
  const systemMode = useSystemColorMode();
  const mode: ColorMode = preference === 'system' ? systemMode : preference;

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  // Keeps native form controls, scrollbars and the address bar in sync.
  useEffect(() => {
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
}
